import { useEffect, useState } from "react";
import { api } from "../utils/api";
import { usePraxis } from "../context/PraxisContext";

/**
 * Admin-Tab "Rechtliches & Kontakt"
 *  - Impressum (Markdown)
 *  - Datenschutz (Markdown)
 *  - E-Mail: Betreff-Vorlage, Signatur, Autoresponder
 *  - Popup „Neu in der Praxis" (Ein/Aus, Verzögerung in Sekunden, Inhalte)
 */
export default function LegalContactManager({ readOnly = false }) {
  const { legal, mail, popup, defaults, refresh } = usePraxis();

  const [values, setValues] = useState({
    legal_impressum: "",
    legal_datenschutz: "",
    mail_subject_template: "",
    mail_signature: "",
    mail_autoresponder: "",
    popup_enabled: "1",
    popup_delay_seconds: "60",
    popup_badge: "",
    popup_title: "",
    popup_text: "",
    popup_bullets: "",
    popup_note: "",
    popup_cta_label: "",
    popup_cta_link: "",
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setValues({
      legal_impressum: legal?.impressum ?? "",
      legal_datenschutz: legal?.datenschutz ?? "",
      mail_subject_template: mail?.subject_template ?? "",
      mail_signature: mail?.signature ?? "",
      mail_autoresponder: mail?.autoresponder ?? "",
      popup_enabled: String(popup?.enabled ?? "1"),
      popup_delay_seconds: String(popup?.delay_seconds ?? 60),
      popup_badge: popup?.badge ?? "",
      popup_title: popup?.title ?? "",
      popup_text: popup?.text ?? "",
      popup_bullets: popup?.bullets ?? "",
      popup_note: popup?.note ?? "",
      popup_cta_label: popup?.cta_label ?? "",
      popup_cta_link: popup?.cta_link ?? "",
    });
  }, [legal, mail, popup]);

  const set = (patch) => setValues((v) => ({ ...v, ...patch }));

  const insertDefault = (key, text) => {
    if (readOnly) return;
    if (!window.confirm(
      "Aktuellen Text durch Standard-Vorlage ersetzen? Nicht gespeicherte Änderungen gehen verloren.")) return;
    set({ [key]: text });
  };

  const save = async () => {
    if (readOnly) return;
    setError(""); setMessage(""); setSaving(true);
    // Nur Zahlen als delay
    const payload = { ...values };
    payload.popup_delay_seconds = String(Math.max(1, parseInt(values.popup_delay_seconds || "60", 10) || 60));
    payload.popup_enabled = values.popup_enabled === "1" ? "1" : "0";
    const { status, data } = await api.updateSettings(payload);
    setSaving(false);
    if (data && data.ok) {
      setMessage("Änderungen gespeichert.");
      refresh();
      setTimeout(() => setMessage(""), 3500);
    } else if (status === 401) {
      setError("Ihre Sitzung ist abgelaufen. Bitte einmal abmelden, neu anmelden und dann erneut speichern.");
    } else {
      setError((data && data.message) || "Speichern fehlgeschlagen.");
    }
  };

  return (
    <div data-testid="legal-contact-manager">
      {readOnly && (
        <p className="admin-align-hint">
          Alle Felder sind editierbar zur Ansicht, aber Änderungen können im Lesemodus nicht gespeichert werden.
        </p>
      )}
      {message && <p className="admin-success">{message}</p>}
      {error && <p className="admin-error">{error}</p>}

      {/* ============ IMPRESSUM ============ */}
      <section className="legal-section">
        <div className="legal-section-header">
          <h2 className="admin-list-title">Impressum</h2>
          <button type="button" className="admin-btn admin-btn-ghost" disabled={readOnly}
                  onClick={() => insertDefault("legal_impressum", defaults.impressum)}
                  data-testid="legal-load-impressum-default">
            Standard-Vorlage einfügen
          </button>
        </div>
        <p className="admin-align-hint">
          Text in <strong>Markdown</strong>: Überschriften mit <code>##</code>, Fett mit <code>**Text**</code>,
          Absätze mit Leerzeile, Aufzählungen mit <code>-</code>, Links wie <code>[Text](https://…)</code>.
          Wenn dieses Feld leer bleibt, wird auf der Website die Standardvorlage angezeigt.
        </p>
        <textarea
          value={values.legal_impressum}
          onChange={(e) => set({ legal_impressum: e.target.value })}
          rows={16}
          className="legal-textarea"
          placeholder="Ihr Impressum-Text (Markdown). Leer lassen für Standardvorlage."
          data-testid="legal-impressum-input"
        />
      </section>

      {/* ============ DATENSCHUTZ ============ */}
      <section className="legal-section">
        <div className="legal-section-header">
          <h2 className="admin-list-title">Datenschutzerklärung</h2>
          <button type="button" className="admin-btn admin-btn-ghost" disabled={readOnly}
                  onClick={() => insertDefault("legal_datenschutz", defaults.datenschutz)}
                  data-testid="legal-load-datenschutz-default">
            Standard-Vorlage einfügen
          </button>
        </div>
        <p className="admin-align-hint">
          Gleiche Formatierung wie Impressum (Markdown). Leer = Standardvorlage.
        </p>
        <textarea
          value={values.legal_datenschutz}
          onChange={(e) => set({ legal_datenschutz: e.target.value })}
          rows={16}
          className="legal-textarea"
          placeholder="Ihre Datenschutzerklärung (Markdown). Leer lassen für Standardvorlage."
          data-testid="legal-datenschutz-input"
        />
      </section>

      {/* ============ E-MAIL-VORLAGEN ============ */}
      <section className="legal-section">
        <h2 className="admin-list-title">E-Mail-Vorlagen</h2>
        <p className="admin-align-hint">
          Diese Texte werden verwendet, wenn Patientinnen über die Website E-Mails senden – aktuell:
          der „Termin vereinbaren"-Button auf der Wechseljahre-Seite (<a href="/wechseljahre" target="_blank" rel="noreferrer">/wechseljahre</a>).
          Die Signatur wird zusätzlich unter Praxis-Benachrichtigungsmails angehängt.
        </p>

        <label className="admin-label">
          Betreff der Terminanfrage (Wechseljahre-Seite)
          <input
            type="text"
            value={values.mail_subject_template}
            onChange={(e) => set({ mail_subject_template: e.target.value })}
            placeholder={defaults.mailSubject}
            maxLength={200}
            data-testid="legal-mail-subject-input"
          />
          <small className="admin-align-hint">
            Wird als Betreff vorausgefüllt, wenn eine Patientin über den „Termin vereinbaren"-Button
            eine E-Mail an die Praxis sendet.
          </small>
        </label>

        <label className="admin-label">
          Text der Terminanfrage (Vorlage für Patientinnen)
          <textarea
            value={values.mail_autoresponder}
            onChange={(e) => set({ mail_autoresponder: e.target.value })}
            rows={12}
            placeholder={defaults.mailAutoresponder}
            data-testid="legal-mail-autoresponder-input"
          />
          <small className="admin-align-hint">
            Wird als Nachrichten-Text vorausgefüllt, wenn eine Patientin über den
            „Termin vereinbaren"-Button auf /wechseljahre die E-Mail öffnet.
            Sie kann den Text dann in ihrem E-Mail-Programm ergänzen/anpassen.
          </small>
        </label>

        <label className="admin-label">
          Signatur (unter Praxis-Benachrichtigungsmails)
          <textarea
            value={values.mail_signature}
            onChange={(e) => set({ mail_signature: e.target.value })}
            rows={6}
            placeholder={defaults.mailSignature}
            data-testid="legal-mail-signature-input"
          />
        </label>
      </section>

      {/* ============ POPUP ============ */}
      <section className="legal-section">
        <h2 className="admin-list-title">Popup „Neu in der Praxis&#8220;</h2>
        <p className="admin-align-hint">
          Erscheint auf der Startseite als Overlay, sobald der Cookie-Consent erteilt wurde
          und die eingestellte Zeit abgelaufen ist. Wird pro Session nur einmal gezeigt.
        </p>

        <div className="admin-two-col">
          <label className="admin-label">
            Popup aktivieren
            <select
              value={values.popup_enabled}
              onChange={(e) => set({ popup_enabled: e.target.value })}
              data-testid="legal-popup-enabled-input"
            >
              <option value="1">Ja – Popup wird angezeigt</option>
              <option value="0">Nein – Popup ist deaktiviert</option>
            </select>
          </label>
          <label className="admin-label">
            Verzögerung bis zum Aufpoppen (Sekunden)
            <input
              type="number"
              min="1"
              max="600"
              value={values.popup_delay_seconds}
              onChange={(e) => set({ popup_delay_seconds: e.target.value })}
              placeholder="60"
              data-testid="legal-popup-delay-input"
            />
            <small className="admin-align-hint">Beispiel: 60 = 1 Minute nach Seitenaufruf.</small>
          </label>
        </div>

        <div className="admin-two-col">
          <label className="admin-label">
            Badge (kleines Etikett oben)
            <input
              type="text"
              value={values.popup_badge}
              onChange={(e) => set({ popup_badge: e.target.value })}
              placeholder={defaults.popup.badge}
              maxLength={80}
              data-testid="legal-popup-badge-input"
            />
          </label>
          <label className="admin-label">
            Titel
            <input
              type="text"
              value={values.popup_title}
              onChange={(e) => set({ popup_title: e.target.value })}
              placeholder={defaults.popup.title}
              maxLength={200}
              data-testid="legal-popup-title-input"
            />
          </label>
        </div>

        <label className="admin-label">
          Beschreibung
          <textarea
            value={values.popup_text}
            onChange={(e) => set({ popup_text: e.target.value })}
            rows={4}
            placeholder={defaults.popup.text}
            data-testid="legal-popup-text-input"
          />
        </label>

        <label className="admin-label">
          Aufzählungspunkte (eine Zeile pro Punkt)
          <textarea
            value={values.popup_bullets}
            onChange={(e) => set({ popup_bullets: e.target.value })}
            rows={5}
            placeholder={defaults.popup.bullets}
            data-testid="legal-popup-bullets-input"
          />
        </label>

        <label className="admin-label">
          Hinweis (klein, kursiv unter der Liste)
          <input
            type="text"
            value={values.popup_note}
            onChange={(e) => set({ popup_note: e.target.value })}
            placeholder={defaults.popup.note}
            maxLength={300}
            data-testid="legal-popup-note-input"
          />
        </label>

        <div className="admin-two-col">
          <label className="admin-label">
            Button-Text (Call-to-Action)
            <input
              type="text"
              value={values.popup_cta_label}
              onChange={(e) => set({ popup_cta_label: e.target.value })}
              placeholder={defaults.popup.cta_label}
              maxLength={80}
              data-testid="legal-popup-cta-label-input"
            />
          </label>
          <label className="admin-label">
            Ziellink des Buttons
            <input
              type="text"
              value={values.popup_cta_link}
              onChange={(e) => set({ popup_cta_link: e.target.value })}
              placeholder={defaults.popup.cta_link}
              maxLength={250}
              data-testid="legal-popup-cta-link-input"
            />
            <small className="admin-align-hint">
              Intern: <code>/wechseljahre</code>, extern: <code>https://…</code>
            </small>
          </label>
        </div>
      </section>

      <div className="admin-actions-row" style={{ marginTop: "1.5rem" }}>
        {message && <p className="admin-success" data-testid="legal-save-success-bottom">{message}</p>}
        {error && <p className="admin-error" data-testid="legal-save-error-bottom">{error}</p>}
        <button type="button" onClick={save} disabled={saving || readOnly}
                className="admin-btn admin-btn-primary"
                data-testid="legal-save">
          {saving ? "Speichere…" : "Alle Änderungen speichern"}
        </button>
      </div>
    </div>
  );
}
